// Model Route domain constants and bitmask decoders.
// Bitmask definitions follow the Canvas OpenAPI contract.

import { CanvasStatus } from '@/api/canvas/types'

/** Route protocol bitmask (route_type). */
export const RouteProtocol = {
  OpenAI: 1,
  Claude: 2,
  All: 3,
} as const

/** Model service type bitmask (model_type). */
export const ModelType = {
  MaaS: 1,
  Instance: 2,
  AutoDeployment: 4,
} as const

/** Model capability bitmask (caps.features). */
export const ModelFeature = {
  Reasoning: 1,
  Vision: 2,
  ToolUse: 4,
  LongContext: 8,
} as const

export function hasFlag(value: number, flag: number): boolean {
  return (value & flag) === flag
}

const MODEL_TYPE_LABELS: [flag: number, label: string][] = [
  [ModelType.MaaS, 'MaaS'],
  [ModelType.Instance, 'Instance'],
  [ModelType.AutoDeployment, 'Auto Deployment'],
]

const MODEL_FEATURE_LABELS: [flag: number, label: string][] = [
  [ModelFeature.Reasoning, 'Reasoning'],
  [ModelFeature.Vision, 'Vision'],
  [ModelFeature.ToolUse, 'Tool Use'],
  [ModelFeature.LongContext, 'Long Context'],
]

function decodeBitmask(value: number, labels: [number, string][]): string[] {
  return labels.filter(([flag]) => hasFlag(value, flag)).map(([, label]) => label)
}

/** model_type bitmask → labels, e.g. 5 → ['MaaS', 'Auto Deployment']. */
export function decodeModelTypeLabels(modelType: number): string[] {
  return decodeBitmask(modelType, MODEL_TYPE_LABELS)
}

/**
 * caps.features bitmask → labels. Returns [] for 0 (capability bits not
 * configured yet on the model catalog — expected until backend backfills data).
 */
export function decodeModelFeatureLabels(features: number): string[] {
  return decodeBitmask(features, MODEL_FEATURE_LABELS)
}

/** Capability labels → bitmask for the /models `caps` filter (unknown labels ignored). */
export function encodeModelFeatureLabels(labels: string[]): number {
  return MODEL_FEATURE_LABELS.filter(([, label]) => labels.includes(label)).reduce(
    (mask, [flag]) => mask | flag,
    0,
  )
}

/** Billing record route_type (0=unknown legacy) → display label. */
export function protocolLabel(routeType: number): string {
  if (routeType === RouteProtocol.OpenAI) return 'OpenAI'
  if (routeType === RouteProtocol.Claude) return 'Claude'
  if (routeType === RouteProtocol.All) return 'OpenAI / Claude'
  return '-'
}

/** UI route status used by the Model Route pages. */
export type UiRouteStatus = 'activated' | 'suspended'

export function routeStatusFromApi(status: CanvasStatus): UiRouteStatus {
  return status === 1 ? 'activated' : 'suspended'
}

export function routeStatusToApi(status: UiRouteStatus): CanvasStatus {
  return status === 'activated' ? 1 : 2
}
